/**
 * Common Responsive Utilities
 *
 * Utilities specifically for generating responsive CSS styles
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import { RESPONSIVE_BREAKPOINTS } from './constants';
import type { ResponsiveAttributes, ResponsiveBreakpoint } from './types';
import { stylesToCSS } from './utils';

/**
 * Generate responsive CSS styles for all breakpoints
 */
export const generateResponsiveCSS = < T >(
	attributes: ResponsiveAttributes< T >,
	selector: string = '',
	styleGenerator: (
		values: T,
		breakpoint: ResponsiveBreakpoint
	) => Record< string, string >,
): string => {
	let css = '';

	// Generate styles for each breakpoint
	( [ 'desktop', 'tablet', 'mobile' ] as ResponsiveBreakpoint[] ).forEach(
		( breakpoint ) => {
			const values = attributes[ breakpoint ];

			if ( ! values ) return;

			const styles = styleGenerator( values, breakpoint );

			if ( Object.keys( styles ).length === 0 ) return;

			const cssRules = stylesToCSS( styles );

			if ( breakpoint === 'desktop' ) {
				// Desktop styles without media query
				css += `${ selector } {\n${ cssRules }\n}\n\n`;
			} else {
				// Tablet and mobile with media queries
				const maxWidth =
					breakpoint === 'tablet'
						? RESPONSIVE_BREAKPOINTS.tablet
						: RESPONSIVE_BREAKPOINTS.mobile;

				css += `@media (max-width: ${ maxWidth }) {\n`;
				css += `  ${ selector } {\n`;
				css += cssRules.replace( /^ {2}/gm, '    ' ); // Add extra indentation
				css += '\n  }\n';
				css += '}\n\n';
			}
		},
	);

	return css.trim();
};

/**
 * Generate media queries with custom content
 */
export const generateMediaQuery = (
	breakpoint: 'tablet' | 'mobile',
	content: string,
): string => {
	const maxWidth = RESPONSIVE_BREAKPOINTS[ breakpoint ];

	return `@media (max-width: ${ maxWidth }) {\n${ content }\n}`;
};

/**
 * Get the appropriate media query for a breakpoint
 */
export const getMediaQuery = (
	breakpoint: ResponsiveBreakpoint,
): string | null => {
	switch ( breakpoint ) {
		case 'desktop':
			return null; // No media query needed for desktop
		case 'tablet':
			return `(max-width: ${ RESPONSIVE_BREAKPOINTS.tablet })`;
		case 'mobile':
			return `(max-width: ${ RESPONSIVE_BREAKPOINTS.mobile })`;
		default:
			return null;
	}
};
